import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Barcode,
  Bot,
  Camera,
  CarFront,
  Check,
  ChevronDown,
  ExternalLink,
  ImagePlus,
  MessageSquare,
  Paperclip,
  Plus,
  RefreshCw,
  Send,
  ShoppingCart,
  X
} from 'lucide-react';
import ProductImage from './ProductImage.jsx';
import { prepareImageForUpload } from '../imageUpload.js';

const API = '/api';
const MAX_MESSAGE_LENGTH = 4000;
const MAX_IMAGE_BYTES = 6 * 1024 * 1024;
const MAX_SOURCE_IMAGE_BYTES = 20 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

async function api(path, options = {}) {
  const response = await fetch(`${API}${path}`, {
    credentials: 'include',
    ...options
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Не удалось выполнить запрос');
  return data;
}

async function apiForm(path, formData, signal) {
  const response = await fetch(`${API}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    signal
  });
  const data = await response.json().catch(() => ({}));
  if (response.status === 413) throw new Error('Изображение слишком большое. Максимум 6 МБ.');
  if (!response.ok) throw new Error(data.error || 'Не удалось отправить сообщение');
  return data;
}

function money(value) {
  return `${new Intl.NumberFormat('ru-RU').format(Number(value || 0))} ₽`;
}

function timeLabel(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function productCountLabel(count) {
  const value = Math.abs(Number(count || 0));
  const lastTwo = value % 100;
  const last = value % 10;
  if (lastTwo >= 11 && lastTwo <= 19) return `${value} товаров`;
  if (last === 1) return `${value} товар`;
  if (last >= 2 && last <= 4) return `${value} товара`;
  return `${value} товаров`;
}

function normalizeMessage(message) {
  return {
    ...message,
    createdAt: message?.createdAt || message?.created_at || '',
    imageName: message?.imageName || message?.image_name || '',
    attachedProducts: Array.isArray(message?.attachedProducts) ? message.attachedProducts : []
  };
}

function InlineText({ text }) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
}

function MessageText({ text }) {
  const lines = String(text || '')
    .replace(/\r/g, '')
    .split('\n');
  const blocks = [];
  let paragraph = [];
  let bullets = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push({ type: 'paragraph', lines: paragraph });
    paragraph = [];
  };
  const flushBullets = () => {
    if (!bullets.length) return;
    blocks.push({ type: 'bullets', lines: bullets });
    bullets = [];
  };

  lines.forEach((line) => {
    const bullet = line.match(/^\s*[-•]\s+(.+)$/);
    if (bullet) {
      flushParagraph();
      bullets.push(bullet[1]);
      return;
    }
    if (!line.trim()) {
      flushParagraph();
      flushBullets();
      return;
    }
    flushBullets();
    paragraph.push(line);
  });
  flushParagraph();
  flushBullets();

  return (
    <div className="ai-message-copy">
      {blocks.map((block, blockIndex) =>
        block.type === 'bullets' ? (
          <ul key={`list-${blockIndex}`}>
            {block.lines.map((line, lineIndex) => (
              <li key={`${line}-${lineIndex}`}><InlineText text={line} /></li>
            ))}
          </ul>
        ) : (
          <p key={`paragraph-${blockIndex}`}>
            {block.lines.map((line, lineIndex) => (
              <React.Fragment key={`${line}-${lineIndex}`}>
                {lineIndex > 0 ? <br /> : null}
                <InlineText text={line} />
              </React.Fragment>
            ))}
          </p>
        )
      )}
    </div>
  );
}

function AssistantMark({ compact = false }) {
  return (
    <span className={`ai-assistant-mark ${compact ? 'compact' : ''}`} aria-hidden="true">
      <Bot size={compact ? 17 : 21} strokeWidth={2.1} />
      {!compact ? <i /> : null}
    </span>
  );
}

function QuickActions({ onPrompt, onPhoto }) {
  return (
    <div className="ai-quick-actions" role="group" aria-label="Быстрый старт">
      <button type="button" onClick={() => onPrompt('Помогите подобрать деталь по VIN: ')}>
        <CarFront size={17} aria-hidden="true" />
        <span>По VIN</span>
      </button>
      <button type="button" onClick={() => onPrompt('Найдите товар по артикулу: ')}>
        <Barcode size={17} aria-hidden="true" />
        <span>По артикулу</span>
      </button>
      <button type="button" onClick={onPhoto}>
        <Camera size={17} aria-hidden="true" />
        <span>Загрузить фото</span>
      </button>
    </div>
  );
}

function WelcomeMessage({ onPrompt, onPhoto }) {
  return (
    <article className="ai-message-row assistant ai-welcome-message">
      <AssistantMark />
      <div className="ai-message-stack">
        <div className="ai-message-bubble assistant">
          <strong className="ai-welcome-title">Здравствуйте! Что нужно подобрать?</strong>
          <p>Пришлите VIN, артикул или фото — проверю каталог и покажу подходящие варианты.</p>
        </div>
        <QuickActions onPrompt={onPrompt} onPhoto={onPhoto} />
      </div>
    </article>
  );
}

function ProductRow({ product, state, quantity = 0, onAdd, onOpen }) {
  const inStock = Number(product.stock || 0) > 0;
  const isPending = state === 'pending';
  const isAdded = state === 'added' || Number(quantity) > 0;
  const isError = state === 'error';

  return (
    <article className="ai-product-row">
      <button
        type="button"
        className="ai-product-main"
        onClick={() => onOpen(product)}
        aria-label={`Открыть товар ${product.vendorCode}: ${product.name}`}
      >
        <span className="ai-product-media">
          <ProductImage src={product.imageUrl} alt="" fallbackIconSize={22} />
        </span>
        <span className="ai-product-copy">
          <b>{product.vendorCode || 'Без артикула'}</b>
          <span>{product.name}</span>
          <small className={inStock ? 'available' : 'unavailable'}>
            {inStock ? `В наличии · ${product.stock} шт.` : 'Нет в наличии'}
          </small>
        </span>
      </button>
      <div className="ai-product-side">
        <strong>{money(product.price)}</strong>
        <div className="ai-product-actions">
          <button
            type="button"
            className={`ai-product-cart ${isAdded ? 'added' : ''} ${isError ? 'retry' : ''}`}
            onClick={() => onAdd(product)}
            disabled={!inStock || isPending}
            aria-live="polite"
          >
            {isPending ? <RefreshCw className="spin" size={16} /> : isAdded ? <Check size={17} /> : <ShoppingCart size={17} />}
            <span>{isPending ? 'Добавляю' : isAdded ? `В корзине: ${Math.max(1, Number(quantity) || 1)}` : isError ? 'Повторить' : inStock ? 'В корзину' : 'Нет в наличии'}</span>
          </button>
          <button
            type="button"
            className="ai-product-open"
            onClick={() => onOpen(product)}
            aria-label={`Открыть карточку ${product.vendorCode}`}
            title="Открыть карточку товара"
          >
            <ExternalLink size={17} aria-hidden="true" />
          </button>
        </div>
      </div>
    </article>
  );
}

function ProductAttachments({ products, cartStates, cartQtyById, onAdd, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? products : products.slice(0, 2);
  const hiddenCount = Math.max(0, products.length - visible.length);

  if (!products.length) return null;
  return (
    <section className="ai-product-attachments" aria-label={`Подобрано ${productCountLabel(products.length)}`}>
      <header>
        <strong>Подобрано {productCountLabel(products.length)}</strong>
      </header>
      <div className="ai-product-list">
        {visible.map((product) => (
          <ProductRow
            key={product.id}
            product={product}
            state={cartStates[product.id]}
            quantity={cartQtyById[product.id] || 0}
            onAdd={onAdd}
            onOpen={onOpen}
          />
        ))}
      </div>
      {products.length > 2 ? (
        <button type="button" className="ai-products-more" onClick={() => setExpanded((value) => !value)}>
          <span>{expanded ? 'Свернуть' : `Показать ещё ${hiddenCount}`}</span>
          <ChevronDown className={expanded ? 'expanded' : ''} size={17} aria-hidden="true" />
        </button>
      ) : null}
    </section>
  );
}

function ChatMessage({ message, cartStates, cartQtyById, onAdd, onOpen }) {
  const assistant = message.role === 'assistant';
  const products = assistant ? message.attachedProducts || [] : [];
  return (
    <article className={`ai-message-row ${assistant ? 'assistant' : 'user'}`}>
      {assistant ? <AssistantMark /> : null}
      <div className="ai-message-stack">
        <div className={`ai-message-bubble ${assistant ? 'assistant' : 'user'}`}>
          {message.text ? <MessageText text={message.text} /> : null}
          {message.imageName ? (
            <span className="ai-message-file"><ImagePlus size={14} /> {message.imageName}</span>
          ) : null}
        </div>
        {products.length ? (
          <ProductAttachments
            products={products}
            cartStates={cartStates}
            cartQtyById={cartQtyById}
            onAdd={onAdd}
            onOpen={onOpen}
          />
        ) : null}
        {message.createdAt ? <time dateTime={message.createdAt}>{timeLabel(message.createdAt)}</time> : null}
      </div>
    </article>
  );
}

function ProcessingMessage({ hasImage }) {
  return (
    <article className="ai-message-row assistant ai-processing-message" role="status" aria-live="polite">
      <AssistantMark />
      <div className="ai-message-bubble assistant">
        <span>{hasImage ? 'Смотрю фото и проверяю каталог' : 'Проверяю каталог'}</span>
        <span className="ai-thinking-dots" aria-hidden="true"><i /><i /><i /></span>
      </div>
    </article>
  );
}

function FilePreview({ file, previewUrl, onRemove, compressionSummary }) {
  return (
    <div className="ai-file-preview">
      <span className="ai-file-preview-media">
        {previewUrl ? <img src={previewUrl} alt="Предпросмотр выбранного изображения" /> : <ImagePlus size={20} />}
      </span>
      <span className="ai-file-preview-copy">
        <b>{file.name}</b>
        <small>
          {Math.max(1, Math.round(file.size / 1024))} КБ
          {compressionSummary ? ` · ${compressionSummary}` : ''}
          {' · готово к отправке'}
        </small>
      </span>
      <button type="button" onClick={onRemove} aria-label="Убрать выбранное фото" title="Убрать фото">
        <X size={17} aria-hidden="true" />
      </button>
    </div>
  );
}

export default function AIChatWidget({
  cartQtyById,
  onSetCartQty,
  navigate,
  open,
  onOpenChange,
  isMobile = false
}) {
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [compressionSummary, setCompressionSummary] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [sendingHasImage, setSendingHasImage] = useState(false);
  const [cartStates, setCartStates] = useState({});
  const panelRef = useRef(null);
  const transcriptRef = useRef(null);
  const composerRef = useRef(null);
  const fileInputRef = useRef(null);
  const returnFocusRef = useRef(null);
  const skipFocusReturnRef = useRef(false);
  const instantNextScrollRef = useRef(true);
  const hydratedRef = useRef(false);
  const hydrationControllerRef = useRef(null);
  const requestRef = useRef({ id: 0, controller: null });
  const compressionRequestRef = useRef(0);

  const filePreviewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);

  useEffect(() => () => {
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
  }, [filePreviewUrl]);

  const focusComposer = useCallback(() => {
    window.requestAnimationFrame(() => composerRef.current?.focus({ preventScroll: true }));
  }, []);

  const removeFile = useCallback(() => {
    compressionRequestRef.current += 1;
    setFile(null);
    setCompressing(false);
    setCompressionSummary('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const acceptFile = useCallback(async (nextFile) => {
    if (!nextFile) return;
    if (!ALLOWED_IMAGE_TYPES.has(String(nextFile.type || '').toLowerCase())) {
      setError('Поддерживаются изображения JPEG, PNG и WebP.');
      return;
    }
    if (nextFile.size > MAX_SOURCE_IMAGE_BYTES) {
      setError('Исходное изображение не должно превышать 20 МБ.');
      return;
    }

    const requestId = compressionRequestRef.current + 1;
    compressionRequestRef.current = requestId;
    setFile(null);
    setCompressing(true);
    setCompressionSummary('');
    setError('');

    try {
      const prepared = await prepareImageForUpload(nextFile);
      if (compressionRequestRef.current !== requestId) return;
      if (prepared.file.size > MAX_IMAGE_BYTES) {
        setError('После сжатия изображение всё ещё больше 6 МБ. Выберите другое фото.');
        return;
      }
      setFile(prepared.file);
      if (prepared.compressed) {
        setCompressionSummary(`сжато с ${Math.max(1, Math.round(prepared.originalSize / 1024))} КБ`);
      }
    } catch {
      if (compressionRequestRef.current !== requestId) return;
      if (nextFile.size <= MAX_IMAGE_BYTES) {
        setFile(nextFile);
        setCompressionSummary('оригинал');
      } else {
        setError('Не удалось сжать изображение до 6 МБ. Выберите другое фото.');
      }
    } finally {
      if (compressionRequestRef.current === requestId) setCompressing(false);
    }
  }, []);

  const loadMessages = useCallback(async (sessionId, signal) => {
    if (!sessionId) return [];
    const data = await api(`/ai/chat/sessions/${sessionId}/messages`, { signal });
    return (data.messages || []).map(normalizeMessage);
  }, []);

  useEffect(() => {
    if (!open || hydratedRef.current) return undefined;
    hydratedRef.current = 'loading';
    const controller = new AbortController();
    hydrationControllerRef.current = controller;
    api('/ai/chat/sessions', { signal: controller.signal })
      .then(async (data) => {
        const latestSession = data.sessions?.[0];
        if (latestSession) {
          const history = await loadMessages(latestSession.id, controller.signal);
          if (controller.signal.aborted) return;
          instantNextScrollRef.current = true;
          setActiveSessionId(latestSession.id);
          setMessages(history);
        }
        hydratedRef.current = true;
      })
      .catch((nextError) => {
        if (hydrationControllerRef.current === controller) hydratedRef.current = false;
        if (nextError.name !== 'AbortError') setError(nextError.message);
      });
    return () => {
      if (hydratedRef.current === 'loading') hydratedRef.current = false;
      if (hydrationControllerRef.current === controller) hydrationControllerRef.current = null;
      controller.abort();
    };
  }, [open, loadMessages]);

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    skipFocusReturnRef.current = false;
    focusComposer();

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const isolatedSiblings = [];
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      const shell = panelRef.current?.closest('.ai-chat-shell');
      const parent = shell?.parentElement;
      if (shell && parent) {
        [...parent.children].forEach((element) => {
          if (!(element instanceof HTMLElement) || element === shell) return;
          isolatedSiblings.push({
            element,
            inert: element.inert,
            ariaHidden: element.getAttribute('aria-hidden')
          });
          element.inert = true;
          element.setAttribute('aria-hidden', 'true');
        });
      }
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onOpenChange(false);
        return;
      }
      if (!isMobile || event.key !== 'Tab' || !panelRef.current) return;
      const focusable = [...panelRef.current.querySelectorAll(
        'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )].filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      isolatedSiblings.forEach(({ element, inert, ariaHidden }) => {
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      const returnTarget = returnFocusRef.current;
      returnFocusRef.current = null;
      if (!skipFocusReturnRef.current && returnTarget?.isConnected) {
        window.requestAnimationFrame(() => returnTarget.focus({ preventScroll: true }));
      }
    };
  }, [open, isMobile, focusComposer, onOpenChange]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const transcript = transcriptRef.current;
    if (!transcript) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    transcript.scrollTo({ top: transcript.scrollHeight, behavior: reducedMotion ? 'auto' : behavior });
  }, []);

  useEffect(() => {
    const transcript = transcriptRef.current;
    if (!open) {
      instantNextScrollRef.current = true;
      return undefined;
    }
    if (!transcript) return undefined;

    const behavior = instantNextScrollRef.current ? 'auto' : 'smooth';
    instantNextScrollRef.current = false;
    const frame = window.requestAnimationFrame(() => scrollToBottom(behavior));
    return () => window.cancelAnimationFrame(frame);
  }, [messages, sending, open, scrollToBottom]);

  useEffect(() => {
    const textarea = composerRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 156)}px`;
  }, [text]);

  useEffect(() => () => {
    hydrationControllerRef.current?.abort();
    requestRef.current.controller?.abort();
  }, []);

  const startNewChat = useCallback(() => {
    hydrationControllerRef.current?.abort();
    hydrationControllerRef.current = null;
    hydratedRef.current = true;
    requestRef.current.id += 1;
    requestRef.current.controller?.abort();
    requestRef.current.controller = null;
    setActiveSessionId(null);
    setMessages([]);
    setText('');
    setSending(false);
    setSendingHasImage(false);
    setError('');
    setCartStates({});
    setDragActive(false);
    removeFile();
    focusComposer();
  }, [focusComposer, removeFile]);

  const choosePrompt = useCallback((prompt) => {
    setText(prompt);
    setError('');
    focusComposer();
  }, [focusComposer]);

  const choosePhoto = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const openProduct = useCallback((product) => {
    if (isMobile) {
      skipFocusReturnRef.current = true;
      onOpenChange(false);
    }
    navigate({ name: 'product', productId: product.id });
  }, [isMobile, navigate, onOpenChange]);

  const addToCart = useCallback(async (product) => {
    setError('');
    setCartStates((current) => ({ ...current, [product.id]: 'pending' }));
    try {
      await onSetCartQty(product, (cartQtyById[product.id] || 0) + 1);
      setCartStates((current) => ({ ...current, [product.id]: 'added' }));
    } catch (nextError) {
      setCartStates((current) => ({ ...current, [product.id]: 'error' }));
      setError(nextError.message || 'Не удалось добавить товар в корзину.');
    }
  }, [cartQtyById, onSetCartQty]);

  const submit = useCallback(async (event) => {
    event?.preventDefault();
    const cleanText = text.trim();
    if ((!cleanText && !file) || sending || compressing) return;

    const requestId = requestRef.current.id + 1;
    const controller = new AbortController();
    const submittedText = cleanText;
    const submittedFile = file;
    hydrationControllerRef.current?.abort();
    hydrationControllerRef.current = null;
    hydratedRef.current = true;
    requestRef.current.controller?.abort();
    requestRef.current = { id: requestId, controller };
    const optimisticMessage = {
      id: `optimistic-${requestId}`,
      role: 'user',
      text: cleanText,
      imageName: file?.name || '',
      attachedProducts: [],
      createdAt: new Date().toISOString()
    };
    setMessages((current) => [...current, optimisticMessage]);
    setSending(true);
    setSendingHasImage(Boolean(submittedFile));
    setError('');

    const formData = new FormData();
    formData.append('text', submittedText);
    if (activeSessionId) formData.append('sessionId', String(activeSessionId));
    if (submittedFile) formData.append('image', submittedFile);
    setText('');
    removeFile();

    try {
      const result = await apiForm('/ai/chat/query', formData, controller.signal);
      if (requestRef.current.id !== requestId) return;
      setActiveSessionId(result.sessionId);

      let history;
      try {
        history = await loadMessages(result.sessionId, controller.signal);
      } catch (historyError) {
        if (historyError.name === 'AbortError' || requestRef.current.id !== requestId) throw historyError;
        const answerText = String(result.answer?.text || '').trim();
        setMessages((current) => [
          ...current,
          ...(answerText
            ? [{
                id: `assistant-${requestId}`,
                role: 'assistant',
                text: answerText,
                imageName: '',
                attachedProducts: result.answer?.attachedProducts || [],
                createdAt: new Date().toISOString()
              }]
            : [])
        ]);
        setError('Ответ получен, но историю чата не удалось обновить. Можно продолжать диалог.');
        return;
      }
      if (requestRef.current.id !== requestId) return;

      if (result.answer?.attachedProducts?.length && history.length) {
        const latestAssistantIndex = history.map((message) => message.role).lastIndexOf('assistant');
        if (latestAssistantIndex >= 0 && !history[latestAssistantIndex].attachedProducts.length) {
          history[latestAssistantIndex] = {
            ...history[latestAssistantIndex],
            attachedProducts: result.answer.attachedProducts
          };
        }
      }
      setMessages(history);
    } catch (nextError) {
      if (nextError.name !== 'AbortError' && requestRef.current.id === requestId) {
        setMessages((current) => current.filter((message) => message.id !== optimisticMessage.id));
        setText((current) => current || submittedText);
        setFile((current) => current || submittedFile);
        setError(nextError.message || 'Ассистент временно недоступен. Попробуйте ещё раз.');
      }
    } finally {
      if (requestRef.current.id === requestId) {
        requestRef.current.controller = null;
        setSending(false);
        setSendingHasImage(false);
      }
    }
  }, [activeSessionId, compressing, file, loadMessages, removeFile, sending, text]);

  return (
    <div className={`ai-chat-shell ${open ? 'open' : ''} ${isMobile ? 'mobile' : ''}`}>
      {isMobile && open ? (
        <button
          type="button"
          className="ai-chat-backdrop"
          onClick={() => onOpenChange(false)}
          aria-label="Закрыть ассистента"
          tabIndex={-1}
        />
      ) : null}
      {open ? (
        <section
          className="ai-chat-panel"
          id="ai-chat-dialog"
          ref={panelRef}
          role="dialog"
          aria-modal={isMobile || undefined}
          aria-labelledby="ai-chat-title"
        >
          <header className="ai-chat-header">
            <div className="ai-chat-identity">
              <AssistantMark compact />
              <div>
                <b id="ai-chat-title">DIXEL Ассистент</b>
                <span>Подбор деталей по VIN, фото и артикулу</span>
              </div>
            </div>
            <div className="ai-header-actions">
              <button
                type="button"
                className="ai-header-new"
                onClick={startNewChat}
                disabled={sending}
                aria-label="Новый диалог"
                title="Новый диалог"
              >
                <Plus size={16} aria-hidden="true" />
                <span>Новый диалог</span>
              </button>
              <button type="button" className="ai-header-icon" onClick={() => onOpenChange(false)} aria-label="Закрыть ассистента" title="Закрыть">
                <X size={20} aria-hidden="true" />
              </button>
            </div>
          </header>

          <div className="ai-chat-body">
            <div
              className="ai-chat-transcript"
              ref={transcriptRef}
              role="log"
              aria-live="polite"
              aria-relevant="additions text"
              aria-busy={sending}
              tabIndex={0}
            >
              {!messages.length ? <WelcomeMessage onPrompt={choosePrompt} onPhoto={choosePhoto} /> : null}
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  cartStates={cartStates}
                  cartQtyById={cartQtyById}
                  onAdd={addToCart}
                  onOpen={openProduct}
                />
              ))}
              {sending ? <ProcessingMessage hasImage={sendingHasImage} /> : null}
            </div>
          </div>

          <form
            className={`ai-chat-form ${dragActive ? 'drag-active' : ''}`}
            onSubmit={submit}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragActive(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              event.preventDefault();
              if (!event.currentTarget.contains(event.relatedTarget)) setDragActive(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setDragActive(false);
              if (sending || compressing) return;
              void acceptFile(event.dataTransfer?.files?.[0] || null);
            }}
          >
            {compressing ? (
              <div className="ai-file-preview" role="status" aria-live="polite">
                <RefreshCw className="spin" size={20} aria-hidden="true" />
                <span className="ai-file-preview-copy"><b>Сжимаю фото…</b><small>Подготавливаю к отправке</small></span>
              </div>
            ) : null}
            {file ? (
              <FilePreview
                file={file}
                previewUrl={filePreviewUrl}
                onRemove={removeFile}
                compressionSummary={compressionSummary}
              />
            ) : null}
            <div className="ai-composer-box">
              <button
                type="button"
                className="ai-composer-attach"
                onClick={choosePhoto}
                disabled={sending || compressing}
                aria-label="Прикрепить фото"
                title="Прикрепить фото"
              >
                <Paperclip size={20} aria-hidden="true" />
              </button>
              <textarea
                ref={composerRef}
                value={text}
                maxLength={MAX_MESSAGE_LENGTH}
                rows={1}
                disabled={sending || compressing}
                aria-label="Сообщение для DIXEL Ассистента"
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    if (event.nativeEvent.isComposing) return;
                    event.preventDefault();
                    void submit();
                  }
                }}
                placeholder="Опишите деталь, VIN или приложите фото"
              />
              <button
                type="submit"
                className="ai-composer-send"
                disabled={sending || compressing || (!text.trim() && !file)}
                aria-label={compressing ? 'Фото сжимается' : sending ? 'Сообщение отправляется' : 'Отправить сообщение'}
              >
                {sending ? <RefreshCw className="spin" size={20} /> : <Send size={21} aria-hidden="true" />}
              </button>
              <input
                ref={fileInputRef}
                className="ai-file-input"
                type="file"
                disabled={sending || compressing}
                tabIndex={-1}
                aria-hidden="true"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => void acceptFile(event.target.files?.[0] || null)}
              />
            </div>
            <div className="ai-composer-meta">
              <span>Enter — отправить · Shift+Enter — новая строка</span>
              <span className={text.length > MAX_MESSAGE_LENGTH * 0.9 ? 'near-limit' : ''}>{text.length}/{MAX_MESSAGE_LENGTH}</span>
            </div>
            {dragActive && !file ? <div className="ai-drop-hint">Отпустите изображение, чтобы прикрепить</div> : null}
            {error ? <div className="ai-chat-error" role="alert">{error}</div> : null}
          </form>
        </section>
      ) : null}

      {!isMobile ? (
        <button
          type="button"
          className="ai-chat-toggle"
          onClick={() => onOpenChange(!open)}
          aria-expanded={open}
          aria-controls="ai-chat-dialog"
        >
          <MessageSquare size={18} aria-hidden="true" />
          <span>Ассистент</span>
        </button>
      ) : null}
    </div>
  );
}
